import { useState, useEffect } from 'react';
import { ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight, Search, MoreVertical, Upload, Trash2, Pencil } from 'lucide-react';
import { useListOnboardingQuery, useAddOnboarding, useUpdateOnboarding, useUpdateStatusOnboarding, useDeleteOnboarding } from '../../hooks/useOnboarding';
import { useS3Upload } from '../../hooks/useCommon';
import { useQueryClient } from '@tanstack/react-query';
import { useListLanguages } from "../../hooks/useLanguages"
import { useStore } from '@tanstack/react-store';
import { selectedLanguageStore, setSelectedLanguage } from '../../stores/selectedLanguage';
import Modal from '../../components/common/ModalComponent';
import toast from 'react-hot-toast';

const Onboarding = () => {
    const { selectedLanguage } = useStore(selectedLanguageStore)
    const [items, setItems] = useState([]);
    const [languages, setLanguages] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showDropdown, setShowDropdown] = useState(null);
    const { REACT_APP_IMG_URL } = process.env
    const uploadMutation = useS3Upload();
    const addMutation = useAddOnboarding();
    const updateMutation = useUpdateOnboarding();
    const deleteMutation = useDeleteOnboarding();
    const updateStatusMutation = useUpdateStatusOnboarding();
    const queryClient = useQueryClient();
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [imagePreview, setImagePreview] = useState(null);
    const [onboardingImage, setOnboardingImage] = useState(null);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [selectedItem, setSelectedItem] = useState(null);
    const { data: onboardingListResponse, isSuccess: isOnboardingListSuccess } = useListOnboardingQuery(`?language=${selectedLanguage}&search_key=${searchTerm}`)
    const { data: languagesListResponse, isSuccess: isSuccessLanguagesList } = useListLanguages('')
    const filePath = "onboarding"

    // Pagination functions
    let totalPages = Math.ceil(items?.filter(item => item?.title?.toLowerCase().includes(searchTerm?.toLowerCase()))?.length / rowsPerPage)
    totalPages = totalPages == 0 ? 1 : totalPages

    const toggleDropdown = (id) => {
        setShowDropdown(showDropdown === id ? null : id);
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setOnboardingImage(file); // store file
            const imageURL = URL.createObjectURL(file);
            setImagePreview(imageURL);
        }
    };

    const UploadToS3 = async () => {
        if (onboardingImage) {
            let response = await uploadMutation.mutateAsync({
                file: onboardingImage,
                filePath
            })
            return response
        } else {
            toast.error("File not found")
            return null
        }
    }

    const toggleStatus = (id, currentStatus) => {
        updateStatusMutation.mutate({
            status: currentStatus == 1 ? 3 : 1,
            _id: id
        }, {
            onSuccess: () => {
                toast.success("Onboarding screen status updated.")
                queryClient.invalidateQueries({ queryKey: ['onboarding_screens'] });
            },
            onError: (error) => {
                const message = error.response?.data?.message
                toast.error(message)
            }
        })
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleAddClick = () => {
        setModalMode('add');
        setShowModal(true);
    };

    const handleEditClick = (item) => {
        setShowDropdown(false);
        setSelectedItem(item)
        setTitle(item?.title)
        setDescription(item?.description)
        setImagePreview(`${REACT_APP_IMG_URL}/${item?.image}`)
        setModalMode('edit');
        setShowModal(true);
    };

    const handleDelete = (item) => {
        setSelectedItem(item)
        setShowDeleteModal(true)
    };

    const resetStates = () => {
        setTitle("")
        setDescription("")
        setImagePreview(null)
        setOnboardingImage(null)
        setSelectedItem(null)
    }

    const handleAddSubmit = async () => {
        const imageResponse = await UploadToS3()
        if (!imageResponse) {
            toast.success('Please Select a Image to Proceed');
            return;
        }
        if (title == "") {
            toast.success('Please enter title to proceed');
            return;
        }
        if (description == "") {
            toast.success('Please enter description to proceed');
            return;
        }
        const onboardingData = {
            description,
            title,
            image: `${imageResponse?.Key}`,
            language: selectedLanguage
        }
        addMutation.mutate(onboardingData, {
            onSuccess: () => {
                toast.success("New Onboarding Screen Added.")
                queryClient.invalidateQueries({ queryKey: ['onboarding_screens'] });
                setShowModal(false);
                resetStates()
            },
            onError: (error) => {
                const message = error.response?.data?.message
                toast.error(message)
            }
        })
    };

    const handleEditSubmit = async () => {
        if (title == "") {
            toast.success('Please enter title to proceed');
            return;
        }
        if (description == "") {
            toast.success('Please enter description to proceed');
            return;
        }
        const onboardingData = {
            description,
            title,
            language: selectedLanguage,
            image: selectedItem?.image,
            _id: selectedItem?._id,
        }
        if (onboardingImage) {
            const imageResponse = await UploadToS3()
            if (!imageResponse) {
                toast.success('Please Select a Image to Proceed');
                return;
            }
            onboardingData.image = `${imageResponse?.Key}`
        }
        updateMutation.mutate(onboardingData, {
            onSuccess: () => {
                toast.success("Onboarding screen data has been updated.")
                queryClient.invalidateQueries({ queryKey: ['onboarding_screens'] });
                setShowModal(false);
                resetStates()
            },
            onError: (error) => {
                const message = error.response?.data?.message
                toast.error(message)
            }
        })
    };

    const confirmDelete = () => {
        deleteMutation.mutate({
            _id: selectedItem?._id
        }, {
            onSuccess: () => {
                toast.success("Onboarding screen has been deleted successfully.")
                queryClient.invalidateQueries({ queryKey: ['onboarding_screens'] });
                setShowDeleteModal(false)
            },
            onError: (error) => {
                const message = error.response?.data?.message
                toast.error(message)
            }
        })
    }

    useEffect(() => {
        if (isSuccessLanguagesList) {
            setLanguages(languagesListResponse?.data?.data?.result)
        }
    }, [languagesListResponse, isSuccessLanguagesList])

    useEffect(() => {
        if (isOnboardingListSuccess) {
            setItems(onboardingListResponse?.data?.data?.result ?? [])
        }
    }, [isOnboardingListSuccess, onboardingListResponse])

    return (
        <div className="flex h-screen bg-white text-gray-800">
            {/* Main Content */}
            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Top Navigation */}
                <div className="bg-white border-b border-gray-200 p-4 flex items-center">
                    <div className="text-sm text-blue-600 flex items-center">
                        <span className="hover:underline cursor-pointer">Dashboard</span>
                        <ChevronRight size={14} className="mx-2 text-gray-400" />
                        <span className="text-gray-700">Onboarding</span>
                    </div>
                </div>
                {/* Content Area */}
                <div className="flex-1 overflow-auto p-6">
                    <div className="flex justify-between items-center mb-4">
                        <div>
                            <h1 className="text-2xl font-bold">Onboarding</h1>
                            <p className="text-gray-500">Manage the Introduction Screens of the App</p>
                        </div>
                        <button
                            className="bg-black hover:bg-gray-800 text-white px-4 py-2 rounded"
                            onClick={handleAddClick}>
                            Create
                        </button>
                    </div>
                    {/* Language Selector */}
                    <div className="mb-6 flex space-x-2">
                        {languages.map((language) => (
                            <button
                                key={language}
                                className={`px-4 py-2 rounded ${selectedLanguage === language?.short_code ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-700'}`}
                                onClick={() => setSelectedLanguage(language?.short_code)}>
                                {language?.name}
                            </button>
                        ))}
                    </div>
                    {/* Search */}
                    <div className="mb-4">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Search by name"
                                className="w-full p-2 pl-10 border border-gray-300 rounded"
                                value={searchTerm}
                                onChange={handleSearch}
                            />
                            <Search size={18} className="absolute left-3 top-3 text-gray-400" />
                        </div>
                    </div>
                    {/* Table */}
                    <div className="border border-gray-200 rounded-md mb-4">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="p-4 text-left text-sm font-medium text-gray-500">S.No</th>
                                    <th className="p-4 text-left text-sm font-medium text-gray-500 flex items-center">Image</th>
                                    <th className="p-4 text-left text-sm font-medium text-gray-500">Title</th>
                                    <th className="p-4 text-left text-sm font-medium text-gray-500">Description</th>
                                    <th className="p-4 text-center text-sm font-medium text-gray-500">Status</th>
                                    <th className="p-4 text-right text-sm font-medium text-gray-500">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {items.map((item, idx) => (
                                    <tr key={item._id} className="hover:bg-gray-50">
                                        <td className="p-4">{idx + 1}</td>
                                        <td className="p-4">
                                            <img
                                                className='w-12 h-12 border rounded cursor-pointer'
                                                src={`${REACT_APP_IMG_URL}/${item?.image}`}
                                            />
                                        </td>
                                        <td className="p-4">{item?.title.toString().substring(0,40)}</td>
                                        <td className="p-4">{item?.description.toString().substring(0,40)}...</td>
                                        <td className="px-4 py-4 text-center">
                                            <button
                                                onClick={() => toggleStatus(item?._id, item?.status)}
                                                className="focus:outline-none">
                                                <div className={`w-12 h-6 rounded-full flex items-center ${item?.status == 1 ? 'bg-gray-900' : 'bg-gray-300'} p-1 transition-colors duration-200`}>
                                                    <div className={`w-4 h-4 rounded-full bg-white transform transition-transform duration-200 ${item.status == 1 ? 'translate-x-6' : 'translate-x-0'}`}></div>
                                                </div>
                                            </button>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="relative inline-block">
                                                <button
                                                    onClick={() => toggleDropdown(item._id)}
                                                    className="p-1 hover:bg-gray-100 rounded-full">
                                                    <MoreVertical size={16} />
                                                </button>
                                                {showDropdown === item._id && (
                                                    <div className="absolute right-0 mt-2 w-48 bg-white rounded shadow-lg border border-gray-200 z-999">
                                                        <div className="py-1">
                                                            <button
                                                                onClick={() => handleEditClick(item)}
                                                                className="flex px-4 py-2 text-sm w-full text-left hover:bg-gray-100">
                                                                <Pencil className='w-4 h-4 mr-1 mt-1' /> Edit
                                                            </button>
                                                        </div>
                                                        <div className="py-1">
                                                            <button
                                                                onClick={() => handleDelete(item)}
                                                                className="flex px-4 py-2 text-sm w-full text-left hover:bg-gray-100">
                                                                <Trash2 className='w-4 h-4 mr-1' />Delete
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {items?.length === 0 && (
                                    <tr>
                                        <td colSpan="6" className="p-4 text-center text-gray-500">
                                            No data found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    {/* Pagination */}
                    <div className="flex justify-end">
                        <div className="flex items-center">
                            <div className="mr-4 flex justify-between">
                                <span className="mt-1 mr-2 text-sm text-gray-500">Rows per page</span>
                                <select
                                    className="w-[55px] border border-gray-300 rounded p-1 text-sm"
                                    value={rowsPerPage}
                                    onChange={(e) => setRowsPerPage(Number(e.target.value))}>
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                            </div>
                            <div className="flex items-center text-sm">
                                <span className="mr-4">Page {currentPage} of {totalPages}</span>
                                <div className="flex">
                                    <button
                                        className="p-1 border border-gray-300 rounded-l hover:bg-gray-100"
                                        onClick={() => setCurrentPage(1)}
                                        disabled={currentPage === 1}>
                                        <ChevronsLeft size={16} />
                                    </button>
                                    <button
                                        className="p-1 border-t border-b border-gray-300 hover:bg-gray-100"
                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                        disabled={currentPage === 1}>
                                        <ChevronLeft size={16} />
                                    </button>
                                    <button
                                        className="p-1 border-t border-b border-gray-300 hover:bg-gray-100"
                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                        disabled={currentPage === totalPages}>
                                        <ChevronRight size={16} />
                                    </button>
                                    <button
                                        className="p-1 border border-gray-300 rounded-r hover:bg-gray-100"
                                        onClick={() => setCurrentPage(totalPages)}
                                        disabled={currentPage === totalPages}>
                                        <ChevronsRight size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Add/Edit Role Modal */}
            <Modal
                title={<h2 className="text-xl font-bold mb-2">{modalMode === 'add' ? 'Add Onboarding' : 'Edit Onboarding'}</h2>}
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                children={
                    <div className="">
                        <p className={`text-sm text-center mb-1 text-gray-800`}>Upload image</p>
                        <div className="mb-6 flex justify-center">
                            <div className="w-20 h-20 border-2 border-dashed border-blue-500 rounded-lg flex flex-col items-center justify-center cursor-pointer">
                                {imagePreview ? (
                                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover rounded-md" />
                                ) : (
                                    <Upload size={24} className="text-blue-600" />
                                )}
                                <input
                                    type='file'
                                    style={{ position: 'absolute', height: "90px", width: '90px', opacity: 0, cursor: 'pointer' }}
                                    accept='image/*'
                                    onChange={handleImageChange}
                                />
                            </div>
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">
                                Title<span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                placeholder="Title"
                                className="w-full text-black p-2 border border-gray-300 rounded"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">
                                Description<span className="text-red-500">*</span>
                            </label>
                            <textarea
                                type="text"
                                placeholder="Description"
                                className="w-full text-black p-2 border border-gray-300 rounded"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </div>
                        <div className="flex justify-end mt-6">
                            <button
                                className="px-4 py-2 border border-gray-300 rounded mr-2 hover:bg-gray-50"
                                onClick={() => setShowModal(false)}>
                                Cancel
                            </button>
                            <button
                                className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700"
                                onClick={modalMode == "add" ? handleAddSubmit : handleEditSubmit}>
                                {modalMode == "add" ? "Create" : "Update"}
                            </button>
                        </div>
                    </div>
                }
            />
            <Modal
                title={<h2 className="text-lg font-semibold text-gray-900">Delete Plan</h2>}
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                children={
                    <div className="bg-white rounded-lg w-full">
                        <p className="text-sm text-gray-600 mb-6">
                            Are you sure you want to delete this Onboarding Screen?
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
                                Cancel
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors">
                                Delete Now
                            </button>
                        </div>
                    </div>
                }
            />
        </div>
    );
};
export default Onboarding;